import { Link } from "react-router-dom";
import { MessageCircle, ArrowRight, Mail } from "lucide-react";
import { Seo } from "@/components/Seo";
import { QuoteForm } from "@/components/QuoteForm";
import { CONTACT_EMAIL, SERVICE_OPTION_BY_SLUG, WHATSAPP_LINK, WHATSAPP_NUMBER } from "@/data/content";
import { useLocation } from "react-router-dom";

export default function Contact() {
  const location = useLocation();
  const preselected = SERVICE_OPTION_BY_SLUG[location.state?.service] || location.state?.serviceLabel || "";

  return (
    <div data-testid="contact-page">
      <Seo
        title="Contact et devis gratuit — Koranex"
        description="Parlez-nous de votre projet : site web, application mobile, SEO ou maintenance. Devis gratuit et sans engagement, réponse rapide."
      />
      <header className="py-16 md:py-20 px-6 max-w-6xl mx-auto text-center">
        <span className="text-xs font-mono-x uppercase text-[#38bdf8] tracking-widest kx-up inline-block">
          Contact
        </span>
        <h1 className="font-display text-4xl sm:text-6xl md:text-7xl text-white leading-none mt-3 mb-6 kx-up delay-100">
          Demandez votre devis gratuit
        </h1>
        <p className="text-base sm:text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed kx-up delay-200">
          Décrivez votre projet en quelques lignes. Nous l'étudions et revenons vers vous rapidement avec une
          proposition claire, gratuite et sans engagement.
        </p>
      </header>

      <section className="pb-16 md:pb-24 px-6 max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
        <div className="lg:col-span-2">
          <QuoteForm formId="contact-quote" defaultService={preselected} />
        </div>

        <aside className="space-y-6">
          <div className="rounded-2xl bg-[#0f172a] border border-slate-800 p-6">
            <h2 className="text-xs font-mono-x uppercase tracking-widest text-[#38bdf8] mb-3">Réponse rapide</h2>
            <p className="text-sm text-slate-300 leading-relaxed mb-4">
              Vous préférez échanger directement ? Contactez-nous sur WhatsApp, nous répondons rapidement.
            </p>
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp-button"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-[#1d4ed8] hover:bg-blue-600 text-white text-xs font-mono-x font-bold uppercase tracking-wider transition-colors"
            >
              <MessageCircle className="w-4 h-4" />
              {WHATSAPP_NUMBER}
            </a>
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              data-testid="contact-email-link"
              className="mt-3 inline-flex items-center gap-2 text-xs font-mono-x text-[#38bdf8] hover:text-white transition-colors"
            >
              <Mail className="w-4 h-4" />
              {CONTACT_EMAIL}
            </a>
          </div>

          <div className="rounded-2xl bg-[#0f172a] border border-slate-800 p-6">
            <h2 className="text-xs font-mono-x uppercase tracking-widest text-[#38bdf8] mb-3">Comment ça se passe</h2>
            <ol className="space-y-3 text-sm text-slate-300">
              <li className="flex gap-3">
                <span className="font-display text-[#1d4ed8]">01</span>
                Vous décrivez votre projet via le formulaire ou WhatsApp.
              </li>
              <li className="flex gap-3">
                <span className="font-display text-[#1d4ed8]">02</span>
                Nous échangeons pour bien comprendre vos objectifs.
              </li>
              <li className="flex gap-3">
                <span className="font-display text-[#1d4ed8]">03</span>
                Vous recevez un devis gratuit, clair et sans engagement.
              </li>
            </ol>
          </div>

          <div className="rounded-2xl bg-[#0f172a] border border-slate-800 p-6">
            <h2 className="text-xs font-mono-x uppercase tracking-widest text-[#38bdf8] mb-3">Pas encore prêt ?</h2>
            <p className="text-sm text-slate-300 leading-relaxed mb-4">
              Parcourez nos concepts de sites et d'applications pour vous inspirer.
            </p>
            <Link
              to="/realisations"
              data-testid="contact-portfolio-link"
              className="inline-flex items-center gap-2 text-xs font-mono-x font-bold uppercase tracking-wider text-[#38bdf8] hover:text-white transition-colors"
            >
              Voir les réalisations <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </aside>
      </section>
    </div>
  );
}
